import { useMemo } from 'react';

import { usePlayers } from '@hooks/usePlayers';
import { useAccessToken } from '@hooks/useAccessToken';
import type { PitchPlayerFixture } from '@components/pitch-player';

type UseSquadFixturesResult = {
  fixturesMap: Map<string, PitchPlayerFixture[]>;
  isLoading: boolean;
};

/**
 * Fetches upcoming fixtures (with predicted points) for a set of squad player
 * ids, keyed by player id. Used by the pre-gameweek-one build flow, where the
 * screenshot import response carries no fixtures and manually picked players
 * lose theirs once reduced to the minimal build shape. An upstream failure
 * yields an empty map, so the pitch simply renders without fixture tiles.
 */
export const useSquadFixtures = (
  squadPlayerIds: string[]
): UseSquadFixturesResult => {
  const { accessToken } = useAccessToken();
  // Normalise before joining so reordering the squad doesn't change the query
  // key and trigger an unnecessary refetch of an identical player set. Mirrors
  // useMyTeamPriceChanges.
  const normalisedIds = [...new Set(squadPlayerIds.filter(Boolean))].sort();
  const ids = normalisedIds.join(',');

  const { data, isFetching } = usePlayers({
    accessToken: accessToken ?? undefined,
    ids: ids.length > 0 ? ids : undefined,
    limit: normalisedIds.length || 1,
    enabled: normalisedIds.length > 0
  });

  const fixturesMap = useMemo(() => {
    const map = new Map<string, PitchPlayerFixture[]>();
    if (!data) return map;
    for (const page of data.pages) {
      for (const player of page.data) {
        if (!player.id) continue;
        map.set(
          String(player.id),
          player.fixtures.map((fixture) => ({
            opponent: { shortName: fixture.opponent.shortName },
            isHome: fixture.isHome,
            predictedPoints: fixture.predictions?.points ?? null
          }))
        );
      }
    }
    return map;
  }, [data]);

  return {
    fixturesMap,
    isLoading: isFetching && normalisedIds.length > 0
  };
};
